/**
 * This file is a utility file for the functionality of the Vendor Application form.
 */

import { every, keys, omit, pick, values } from 'lodash';
import VendorNSUpdateFields from '../../Constants/ns-vendor-update-fields';
import {
  getApplicationStatus,
  isValidAccountNumber,
  isValidRoutingNumber,
} from '../FormUtils/vendor-application';
import ApplicationStatus from '../../Constants/vendor-application-status';
import isDomestic from '../VendorApplication/is-domestic';

/**
 * creates an object of all of the required fields on the Vendor Application
 *
 * @param {object} form the fields and values from the form
 * @param {boolean} update if the form is an update to an existing vendor
 *
 * @returns {object} the application fields that are required for the scenario
 */
export function findRequiredFields(form) {
  let fieldsToCheck = omit(form, [
    'invited_by',
    'invited_by_user',
    'invite_token_id',
    'address_2',
    'payment_billing_address_2',
    'submitted_at',
    'portal_message',
    'default_delivery_location',
    'application_status',
    'application_archived_at',
    'fcr_expiration',
    'certificate_of_insurance',
    'coi_expiration_date',
    'coi_not_available',
    'grade',
    'grade_updated_at',
    'attributes',
    'approved_at',
    'approved_by',
    'w8_form',
    'ssn',
    'wire_account_name',
    'wire_routing_number',
    'wire_account_number',
    'wire_bank_name',
    'proposition65',
    'payment_term',
    'middle_name',
    'is_individual',
    'invite_token',
    'approval_signatures',
    'approval_signed_by',
    'vendor_agreement',
    'vendor_documents',
    'custom_payment_terms',
    'beauty_vendor_agreement',
    'manufacturing_type',
    'divisions',
    'sponsor_name',
    'is_direct',
    'merch_type',
    'sponsor_id',
    'existing_vendor',
    'requested_item_lead_times_count',
    'influencer_handle',
    'influencer_platform',
    'w8bene_form',
    'w8ben_form',
    'w8_form',
    'w9_form',
  ]);

  if (!isDomestic(form.country)) {
    fieldsToCheck = omit(fieldsToCheck, ['tax_id', 'state']);
  }

  if (!isDomestic(form.payment_country)) {
    fieldsToCheck = omit(fieldsToCheck, ['payment_state']);
  }

  if (isDomestic(form.country)) {
    fieldsToCheck = omit(fieldsToCheck, [
      'w8_form',
      'w8ben_form',
      'w8bene_form',
    ]);
  }

  if (form.is_individual) {
    fieldsToCheck = omit(fieldsToCheck, ['company_name', 'legal_name']);
  } else {
    fieldsToCheck = omit(fieldsToCheck, [
      'company_name',
      'first_name',
      'last_name',
    ]);
  }

  fieldsToCheck.reEnterAccountNumber =
    form.reEnterAccountNumber === form.account_number || null;
  fieldsToCheck.reEnterRoutingNumber =
    form.reEnterRoutingNumber === form.routing_number || null;

  return fieldsToCheck;
}

export function validDomesticBankFields(form) {
  const domestic = isDomestic(form.payment_country);

  if (!domestic) return true;

  return (
    isValidRoutingNumber(form.routing_number, domestic) &&
    isValidAccountNumber(form.account_number, domestic)
  );
}

/**
 * Checks if all required fields for the form are filled on submit
 * for creating a vendor in NetSuite
 * @param {object} form all of the fields and values from the form
 * @param {boolean} hasWirePayment whether the wire payment fields were enabled
 *
 * @returns {boolean} whether all required fields were filled out
 */
function validateFormForCreate(form) {
  const requiredFields = findRequiredFields(form);

  return validDomesticBankFields(form) && every(values(requiredFields));
}

/**
 * Checks if all required fields for the form are filled on submit
 * for updating a vendor in NetSuite
 * @param {object} form all of the fields and values from the form
 *
 * @returns {boolean} whether all required fields were filled out
 */
function validateFormForUpdate(form) {
  const fields = pick(form, keys(VendorNSUpdateFields));
  return every(values(fields));
}

/**
 * Checks to see if the application has been updated.
 */
function isUpdate(vendor) {
  const applicationStatus = getApplicationStatus(vendor);
  return applicationStatus === ApplicationStatus.APPROVED;
}

export default function disableSubmit(form, vendor) {
  return isUpdate(vendor)
    ? !validateFormForUpdate(form)
    : !validateFormForCreate(form);
}
